import { useCallback, useEffect, useState } from 'react';

import { useIsUserPermittedTo } from '@occc/utils-access-control';
import { Feature } from '@occc/utils-access-control';
import { snackbar } from '@occc-app/design-system';

import { extractMemberUuidsFromInputValue } from '../../segments.utils';
import {
  useAddMembershipToSegment,
  useRemoveMembershipFromSegment,
} from '../../hooks/segments.mutation.hook';

type UseSegmentAssignMembershipsControllerParams = {
  uuid: string;
  membershipCount: number;
  onLoadingStateChange?: (loading: boolean) => void;
  onMembershipsChanged?: (baselineMembershipCount: number) => void;
};
const onAddMembershipToSegmentCompleted = () => {
  snackbar.success('Members were successfully added to the segment');
};
const onRemoveMembershipFromSegmentCompleted = () => {
  snackbar.success('Members were successfully removed from the segment');
};
export const useSegmentAssignMembershipsController = ({
  uuid,
  membershipCount,
  onLoadingStateChange,
  onMembershipsChanged,
}: UseSegmentAssignMembershipsControllerParams) => {
  const [membershipInputValue, setMembershipInputValue] = useState('');
  const [isRemoveMembersConfirmationOpen, setIsRemoveMembersConfirmationOpen] = useState(false);

  const isUserPermittedToEditSegment = useIsUserPermittedTo(Feature.SEGMENT_EDIT);

  const [addMembersToSegment, { loading: isAddingToSegmentInProgress }] = useAddMembershipToSegment(
    {
      refetchQueries: ['segment'],
      onCompleted: onAddMembershipToSegmentCompleted,
    },
  );
  const [removeMembersFromSegment, { loading: isRemovingFromSegmentInProgress }] =
    useRemoveMembershipFromSegment({
      refetchQueries: ['segment'],
      onCompleted: onRemoveMembershipFromSegmentCompleted,
    });

  useEffect(() => {
    onLoadingStateChange?.(isAddingToSegmentInProgress || isRemovingFromSegmentInProgress);
  }, [isAddingToSegmentInProgress, isRemovingFromSegmentInProgress, onLoadingStateChange]);

  const onAddMembersToSegment = useCallback(
    async (membersInputValue: string) => {
      const membershipUuids = extractMemberUuidsFromInputValue(membersInputValue);
      const response = await addMembersToSegment({
        variables: { input: { segmentId: uuid, memberships: membershipUuids } },
      });

      return response.data?.segmentMembershipAdd ?? false;
    },
    [addMembersToSegment, uuid],
  );

  const onRemoveMembersFromSegment = useCallback(
    async (membersInputValue: string) => {
      const membershipUuids = extractMemberUuidsFromInputValue(membersInputValue);
      const response = await removeMembersFromSegment({
        variables: { input: { segmentId: uuid, memberships: membershipUuids } },
      });

      return response.data?.segmentMembershipDelete ?? false;
    },
    [removeMembersFromSegment, uuid],
  );

  const onRemoveMembersFromSegmentClick = useCallback(() => {
    setIsRemoveMembersConfirmationOpen(true);
  }, []);

  const onRemoveMembersConfirmationClose = useCallback(() => {
    setIsRemoveMembersConfirmationOpen(false);
  }, []);

  const addMembers = async () => {
    const isCompletedSuccessfully = await onAddMembersToSegment(membershipInputValue);

    if (isCompletedSuccessfully) {
      setMembershipInputValue('');
      onMembershipsChanged?.(membershipCount);
    }
  };

  const removeMembers = async () => {
    onRemoveMembersConfirmationClose();
    const isCompletedSuccessfully = await onRemoveMembersFromSegment(membershipInputValue);

    if (isCompletedSuccessfully) {
      setMembershipInputValue('');
      onMembershipsChanged?.(membershipCount);
    }
  };

  return {
    loading: isAddingToSegmentInProgress || isRemovingFromSegmentInProgress,
    isEditAllowed: isUserPermittedToEditSegment,
    onAddMembersToSegment,
    onRemoveMembersFromSegment,
    addMembers,
    removeMembers,
    membershipInputValue,
    setMembershipInputValue,
    isRemoveMembersConfirmationOpen,
    onRemoveMembersFromSegmentClick,
    onRemoveMembersConfirmationClose,
  };
};
